// CPU env samplers for the BecomingLit shading equation (shadeGaussiansEnvmap).
// BOTH diffuse and specular sample the ENG-561 baked light-probe VOLUME — the
// SH grid LightProbeVolume installs as a THREE.LightProbe. No reflection-capture
// images, no runtime cube build: the volume IS the env, and an SH probe can
// answer both queries because it's a (band-limited) radiance field.
//
//   diffuseEnv(n)            = SH IRRADIANCE at the normal (cosine-lobe
//                             convolved), ×1/π → Lambertian light_col.
//   specularEnv(r, rough)    = SH RADIANCE at the reflection vector, with the
//                             higher SH bands attenuated by a roughness-driven
//                             heat-kernel (rough → blurrier → lower bands fade).
//
// Real SH basis constants (degree ≤2) match the baker's sh9basis exactly
// (lightmap-baking-service/sh.go), so reconstruction is faithful.

export const INV_PI = 1 / Math.PI

// Raw real-SH basis constants (no cosine-lobe convolution) — radiance.
const Y0 = 0.282095
const Y1 = 0.488603
const Y2 = 1.092548
const Y6 = 0.315392
const Y8 = 0.546274

// Heat-kernel band attenuation strength: specular blur ∝ exp(-l(l+1)·roughness²·GAMMA).
const SPEC_GAMMA = 1.5

const resolveScale = (scale, fallback) =>
  (scale && typeof scale === 'object') ? scale.value : (scale ?? fallback)

// SH IRRADIANCE at a normal (three.js shGetIrradianceAt band factors). sh27 is
// the baked-grid `probes[].sh` layout: 9 coefficients × interleaved RGB.
export function shIrradianceInto(sh27, nx, ny, nz, outRGB, scale = INV_PI) {
  const c0 = 0.886227          // π · Y0
  const c1 = 2.0 * 0.511664    // band-1 cosine-lobe factor
  const c2 = 2.0 * 0.429043    // band-2 cosine-lobe factor
  const c6 = 0.743125 * nz * nz - 0.247708
  const c8 = 0.429043 * (nx * nx - ny * ny)
  for (let ch = 0; ch < 3; ch++) {
    let v = sh27[ch] * c0
    v += sh27[3 + ch] * c1 * ny
    v += sh27[6 + ch] * c1 * nz
    v += sh27[9 + ch] * c1 * nx
    v += sh27[12 + ch] * c2 * nx * ny
    v += sh27[15 + ch] * c2 * ny * nz
    v += sh27[18 + ch] * c6
    v += sh27[21 + ch] * c2 * nx * nz
    v += sh27[24 + ch] * c8
    outRGB[ch] = v * scale
  }
}

// SH RADIANCE in a direction, with per-band weights (w1 for l=1, w2 for l=2).
// w1=w2=1 → full L2 reconstruction (sharpest the probe allows); fading them
// blurs the field. Negatives (SH ringing) are the caller's to clamp.
export function shRadianceInto(sh27, dx, dy, dz, outRGB, w1 = 1, w2 = 1, scale = 1) {
  for (let ch = 0; ch < 3; ch++) {
    let v = sh27[ch] * Y0
    v += w1 * Y1 * (sh27[3 + ch] * dy + sh27[6 + ch] * dz + sh27[9 + ch] * dx)
    v += w2 * (
      Y2 * (sh27[12 + ch] * dx * dy + sh27[15 + ch] * dy * dz + sh27[21 + ch] * dx * dz) +
      Y6 * sh27[18 + ch] * (3 * dz * dz - 1) +
      Y8 * sh27[24 + ch] * (dx * dx - dy * dy)
    )
    outRGB[ch] = v * scale
  }
}

// Snapshot a THREE.LightProbe's 9 RGB SH coefficients into a flat sh27 buffer.
export function lightProbeToSH27(lightProbe, out27) {
  const out = out27 ?? new Float32Array(27)
  const c = lightProbe.sh.coefficients
  for (let i = 0; i < 9; i++) {
    out[i * 3] = c[i].x
    out[i * 3 + 1] = c[i].y
    out[i * 3 + 2] = c[i].z
  }
  return out
}

// Re-layout the volume probe's degree-2 SH (sh27, interleaved 9·RGB) into the
// degree-3 coeff48 layout the NeuralBSDF expects (R[0..15], G[16..31],
// B[32..47]). The L3 band (k=9..15 per channel) has no volume-probe source, so
// it stays zero — a band-limited approximation of the env the net was trained
// on. The probe's 9 coeffs slot straight in: the baker's sh3Basis ≡ the
// three.js radiance basis shRadianceInto uses, so no reweighting is needed.
export function sh27ToCoeff48(sh27, out48) {
  const out = out48 ?? new Float32Array(48)
  out.fill(0)
  for (let k = 0; k < 9; k++) {
    out[k] = sh27[3 * k]
    out[16 + k] = sh27[3 * k + 1]
    out[32 + k] = sh27[3 * k + 2]
  }
  return out
}

const _driftScratch = new Float32Array(27)

// Relative drift between a live probe and a previous sh27 snapshot: the peak
// per-coefficient change divided by the peak coefficient magnitude. Dividing by
// the peak (not the norm) makes it scale-invariant, so the same fractional move
// trips the same threshold at any overall brightness. Used to throttle re-shades
// and the costlier NeuralBSDF re-run as the avatar moves through the grid.
export function lightProbeSHDrift(lightProbe, prevSh27) {
  const cur = lightProbeToSH27(lightProbe, _driftScratch)
  let peak = 1e-6
  let maxDelta = 0
  for (let i = 0; i < 27; i++) {
    const a = Math.abs(prevSh27[i])
    if (a > peak) peak = a
    const d = Math.abs(cur[i] - prevSh27[i])
    if (d > maxDelta) maxDelta = d
  }
  return maxDelta / peak
}

// diffuseEnv closure: reads `sh27` live; `scale` may be a number or {value} ref
// (live-tunable). Returns Lambertian light_col = irradiance/π by default.
export function makeSHDiffuseSampler(sh27, scale = INV_PI) {
  return (nx, ny, nz, outRGB) => shIrradianceInto(sh27, nx, ny, nz, outRGB, resolveScale(scale, INV_PI))
}

// specularEnv closure: reads `sh27` live. Roughness attenuates the higher SH
// bands (heat kernel), so roughness 0 → sharpest SH reflection, roughness 1 →
// ambient-only. Clamps negatives. `scale` may be a number or {value} ref.
export function makeSHSpecularSampler(sh27, scale = 1) {
  return (rx, ry, rz, roughness, outRGB) => {
    const s = roughness * roughness * SPEC_GAMMA
    const w1 = Math.exp(-2 * s) // l=1 → l(l+1)=2
    const w2 = Math.exp(-6 * s) // l=2 → l(l+1)=6
    shRadianceInto(sh27, rx, ry, rz, outRGB, w1, w2, resolveScale(scale, 1))
    if (outRGB[0] < 0) outRGB[0] = 0
    if (outRGB[1] < 0) outRGB[1] = 0
    if (outRGB[2] < 0) outRGB[2] = 0
  }
}
